/**
 * The router-root error boundary.
 *
 * It renders **the same surface `/500` renders** rather than a second one of
 * its own. Two error surfaces in one repo drift the first time either is
 * edited, and the one a reader meets after a real failure would be the one
 * nobody reviewed.
 *
 * What this caller adds to that surface is the one thing it alone knows: the
 * mapped sentence from the shared catalog. Never a correlation id, never a
 * stack trace, never an env value, never an internal hostname, and never a
 * status code.
 *
 * **IT ALSO CARRIES THE PAGE FRAME, and that is the fix rather than a
 * flourish.** `errorElement` sits on the LAYOUT route, so React Router renders
 * this INSTEAD of `<AppShell>` — no page background, no `.container`, no
 * gutter, no height. `ErrorSurface` is written for the inside of that shell
 * (`h-full` against a column the shell makes definite, no padding of its own,
 * because the container supplies it), so outside it the heading and the status
 * mark ran flush into the viewport's own edge with the page's ink over the
 * browser's default white. That is what a reader met on every crashed route.
 *
 * The frame below is the shell's own outer box minus the chrome — the same
 * `min-h-dvh` column, the same `bg-bg` / `text-ink` pair, the same
 * `.container` — because the failure that lands here may well be the chrome
 * itself, and composing a top bar that has just thrown asks the broken part to
 * draw the recovery.
 *
 * This file is composition, not product code — it resolves a thrown value and
 * hands the feature its props.
 */
import { useRouteError } from 'react-router-dom';

import { describeError } from '@shared/errors/catalog';

import { ServerErrorPage } from '@features/system/500/ServerErrorPage';

export function RootErrorFallback() {
  const error = useRouteError();
  const { message } = describeError(error);

  return (
    <div className="flex min-h-dvh flex-col bg-bg text-ink">
      {/*
        `id="main"` is kept even with no skip link above it: the shell's link
        is gone with the shell, and an anchored landmark still costs nothing and
        is where a hash from the failed page would land.
      */}
      <main id="main" className="container flex flex-[1_0_auto] flex-col">
        <ServerErrorPage reason={message} />
      </main>
    </div>
  );
}
